import { useAccessStore } from '@vben/stores';

/**
 * 跨标签页 accessToken 同步
 *
 * 后端 /auth/refresh 是一次性轮换（刷新成功即删旧 token，并更新设备绑定的
 * current_token_id），同一浏览器的多个标签页共用同一台设备身份但各自持有
 * 独立的内存 token 副本：先刷新的标签页会把其余标签页的 token 顶成失效，
 * 表现为"正在使用中被踢回登录页"。
 *
 * 这里监听 access store 持久化键的 storage 事件，把其它标签页写入的新
 * token 同步进本页内存状态，使一次轮换对多标签页收敛生效。
 */

const STORE_KEY_SUFFIX = '-core-access';

/**
 * 独立 Refresh Token 的明文持久层键
 *
 * 与 pinia persist（DEV 明文 / PROD SecureLS 加密）不同，这里用独立明文键：
 * 跨标签页竞态时需要在本标签页直接读取其它标签页刚轮换写入的最新凭证，
 * SecureLS 加密值无法跨实例直接解析；明文键与 token_created_at 同级，
 * 风险面与 access token 已有的 localStorage 存储一致。
 */
const REFRESH_TOKEN_STORAGE_KEY = 'bayes-mes-refresh-token';

export function clearPersistedRefreshToken(): void {
  try {
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    // localStorage 不可用（隐私模式等）时放弃
  }
}

export function readPersistedRefreshToken(): null | string {
  try {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function savePersistedRefreshToken(token: null | string): void {
  try {
    if (token) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
    } else {
      clearPersistedRefreshToken();
    }
  } catch {
    // localStorage 不可用（隐私模式等）时放弃
  }
}

function findAccessTokenStorageKey(): null | string {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.endsWith(STORE_KEY_SUFFIX)) return key;
    }
  } catch {
    // localStorage 不可用（隐私模式等）时放弃同步
  }
  return null;
}

/**
 * 读取持久化在 localStorage 中的 accessToken（其它标签页可能刚写入的新值）
 */
export function readPersistedAccessToken(): null | string {
  try {
    const key = findAccessTokenStorageKey();
    if (!key) return null;

    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;

    const token = (parsed as Record<string, unknown>).accessToken;
    return typeof token === 'string' && token ? token : null;
  } catch {
    return null;
  }
}

/**
 * 在 bootstrap 完成 pinia 初始化后调用
 */
export function setupCrossTabTokenSync(): void {
  window.addEventListener('storage', (event) => {
    if (!event.key || !event.key.endsWith(STORE_KEY_SUFFIX)) return;

    // 另一标签页登出（键被移除/置空）时，本页同步清空；有新 token 时采纳
    let token: null | string = null;
    if (event.newValue) {
      try {
        const parsed = JSON.parse(event.newValue) as unknown;
        const value =
          parsed && typeof parsed === 'object'
            ? (parsed as Record<string, unknown>).accessToken
            : undefined;
        if (typeof value === 'string' && value) token = value;
      } catch {
        return;
      }
    }

    const accessStore = useAccessStore();
    if (token !== accessStore.accessToken) {
      accessStore.setAccessToken(token);
    }
  });

  // 独立 Refresh Token 的跨标签页同步：其它标签页完成轮换后，本页内存
  // 状态跟进最新凭证，避免下一次刷新携带已轮换的旧凭证
  window.addEventListener('storage', (event) => {
    if (event.key !== REFRESH_TOKEN_STORAGE_KEY) return;

    const accessStore = useAccessStore();
    const token = event.newValue || null;
    if (token !== accessStore.refreshToken) {
      accessStore.setRefreshToken(token);
    }
  });
}
